import { AudioClips } from "../../audio/audio_constants.js";
import { audioController } from "../../audio/audio_controller.js";
import { Vector2 } from "../../common/vector2.js";
import { Game } from "../../game.js";
import { OverlayFactory } from "../overlay/overlay_factory.js";
import { OverlayId } from "../overlay/overlay_ids.js";
import { CoinCounterPill, TopCounterLayout } from "./coin_counter_pill.js";
import { UIElement } from "./ui_element.js";

/**
 * The menu's top coin counter, shown by purchase dialogs so the player sees
 * their balance while buying. Sits on the same screen spot as the menu one, so
 * it is placed relative to the dialog's own origin.
 *
 * A tap opens the coin shop as an overlay (not the Shop tab): the dialog and
 * whatever is under it stay alive, and closing the shop returns right here.
 * The badge lies outside the dialog body, so DialogManager routes taps to it
 * through hitGlobal() before its usual inside/outside check.
 */
export class DialogCoinsBadge extends CoinCounterPill
{
  private static readonly SYNC_INTERVAL_MS = 250;

  private next_sync_at = 0;

  constructor( ctx: CanvasRenderingContext2D, dialog: UIElement, on_shop_closed?: () => void )
  {
    const dialog_pos = dialog.pixelPosImportant();
    super(
      ctx,
      Game.safeAreaLeft() + TopCounterLayout.coinX() - dialog_pos.x,
      Game.safeAreaTop() + TopCounterLayout.y() - dialog_pos.y,
      { clickable: true }
    );

    this.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      OverlayFactory.openOverlay( OverlayId.SHOP_OVERLAY, {
        onShopClosed: () =>
        {
          this.next_sync_at = 0;
          on_shop_closed?.();
        },
      } );
    } );
  }

  public sync( now: number ): void
  {
    if ( now < this.next_sync_at )
      return;
    this.next_sync_at = now + DialogCoinsBadge.SYNC_INTERVAL_MS;
    this.syncCoins();
  }

  /** Whether a screen-space point is on the badge (icon included). */
  public hitGlobal( p: Vector2 ): boolean
  {
    const r = this.getAbsoluteRect();
    return p.x >= r.x && p.x <= r.x + r.width && p.y >= r.y && p.y <= r.y + r.height;
  }

  /** A screen-space point in the coordinates of the badge's parent (what onTouchStart/End take). */
  public toParentSpace( p: Vector2 ): Vector2
  {
    const parent = this.getParentElement();
    const origin = parent ? parent.getAbsolutePosition() : new Vector2( 0, 0 );
    return new Vector2( p.x - origin.x, p.y - origin.y );
  }
}
